// A workspace's page, signed out (02-DESIGN/03-console/01-the-console.md §
// signing in): *Sign in to Acme*, what Acme signs in with, and one button for
// the one provider it signs in through (decision 0055) — its own or its
// managed one by its display name from the profile, Impire's otherwise — and, under
// the card, the address in monospace and the way to sign in somewhere else.
// The address the person arrived at is returned to after the flow. A pick
// from another workspace's menu that this one did not admit lands here with
// the refusal's sentence (01-the-console.md § the shape: the workspace menu).

import { useState } from "react";
import { Link, useLocation } from "react-router";

import { consoleAddress, PLATFORM_NAME, providerName, workspaceName } from "../../platform/names.ts";
import type { TenantProfile } from "../../platform/profile.ts";
import { usePage } from "../../platform/session.tsx";
import { begin } from "../../signin/flow.ts";
import { choices, type Choice } from "../../signin/provider.ts";
import { Button, describe, Notice } from "../../ui/components.tsx";
import { Entry } from "./Entry.tsx";

export function WorkspaceSignIn({ tenant, refused }: { tenant: TenantProfile; refused?: string }) {
  const { origin, platform } = usePage();
  const location = useLocation();
  const [err, setErr] = useState<unknown>(undefined);
  const [busy, setBusy] = useState(false);
  const name = workspaceName(tenant.slug, tenant.name);

  let offered: Choice[] = [];
  let choiceErr: unknown;
  try {
    offered = choices(origin, platform, tenant);
  } catch (e) {
    choiceErr = e;
  }
  const first = offered[0];
  const signsInWith = first === undefined || first.platform ? PLATFORM_NAME : providerName(first.provider);

  const signIn = (c: Choice) => {
    setBusy(true);
    begin(origin, c, location.pathname + location.search).then(
      (to) => {
        window.location.assign(to);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };

  return (
    <Entry
      under={
        <>
          <span className="mono faint xs">{consoleAddress(origin, tenant.slug)}</span>
          <p className="small faint">
            Not {name}?{" "}
            <Link className="text-link" to="/">
              Sign in somewhere else
            </Link>
          </p>
        </>
      }
    >
      <div className="entry-card">
        <h1>Sign in to {name}</h1>
        <p className="lead">
          {name} signs in with {signsInWith}.
        </p>
        {refused !== undefined && <Notice kind="bad">{refused}</Notice>}
        {err !== undefined && <Notice kind="bad">{describe(err)}</Notice>}
        {choiceErr !== undefined && <Notice kind="bad">{describe(choiceErr)}</Notice>}
        <div className="rows">
          {offered.map((c, i) => (
            <Button
              key={c.label}
              kind={i === 0 ? "primary" : "default"}
              size="lg"
              block
              disabled={busy}
              onClick={() => {
                signIn(c);
              }}
            >
              {c.label}
            </Button>
          ))}
          {offered.length === 0 && choiceErr === undefined && (
            <p className="muted">{name} offers no way for a browser to sign in.</p>
          )}
        </div>
      </div>
    </Entry>
  );
}
